import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import { useForm } from 'react-hook-form';
import FormProvider from 'src/components/hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosPostCall } from 'src/services/apisCall';
import UploadArea from 'src/components/upload/upload-area';

const FieldHeading = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: '1.43',
  marginLeft: '0.25rem',
}));

const BulkTemplateForm = ({ setBulkModelOpen }) => {
  const [bulkTemplateFile, setBulkTemplateFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [user, setUser] = useState('');

  const methods = useForm({
    defaultValues: {},
  });

  const { handleSubmit, reset } = methods;

  const handleDownloadErrors = () => {
    if (!uploadErrors || uploadErrors.length === 0) return;

    const headers = ['row', 'title', 'language', 'template', 'Error'];
    const csvContent = [
      headers.join(','),
      ...uploadErrors.map((err) =>
        `"${err.row}","${err.title}","${err.language}","${err.template}","${err.error}"`
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_upload_errors.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    const sampleData = [
      {
        title: 'Welcome Template',
        language: 'english',
        template: 'Welcome to our service.',
      },
      {
        title: 'Special Template',
        language: 'hindi',
        template: 'Special offer just for you! Get 20% off on your next purchase.',
      },
    ];
    const headers = ['title', 'language', 'template'];

    const csvContent = [
      headers.join(','),
      ...sampleData.map((row) => `"${row.title}","${row.language}","${row.template}"`),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setBulkTemplateFile(newFile);
    }
  }, []);

  const onSubmit = async () => {
    if (!user) {
      renderErrorMessage('Please enter the user ID.');
      return;
    }
    if (!bulkTemplateFile) {
      renderErrorMessage('Please upload a CSV file.');
      return;
    }
    if (bulkTemplateFile.size === 0) {
      renderErrorMessage('File is empty. Please select a valid file.');
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', bulkTemplateFile);
    formData.append('userId', user);
    setLoadingSubmit(true);

    try {
      const response = await axiosPostCall('addBulkTemplate', formData);
      renderSuccessMessage(response.message || 'Bulk templates uploaded successfully!');
      // setLoad((prev) => !prev);
      setBulkTemplateFile(null);
      setUploadErrors(null);
      if (response?.results?.failedCount > 0) {
        setUploadErrors(response?.results?.failures);
      } else {
        setBulkModelOpen(false);
        reset();
      }
    } catch (error) {
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setUploadErrors(error.response.data.errors);
        renderErrorMessage(error.message);
      } else {
        setUploadErrors(null);
        renderErrorMessage(error.message || 'Failed to upload bulk templates.');
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <FieldHeading>User</FieldHeading>
          <TextField
            id="outlined-basic"
            variant="outlined"
            placeholder="Enter the user ID for whom you want to upload this template file"
            value={user}
            required
            onChange={(e) => setUser(e.target.value)}
            fullWidth
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
          }}
        >
          <FieldHeading>Templates</FieldHeading>
          <Tooltip
            placement="right"
            arrow
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2,
                    fontSize: '0.75rem',
                    '& li': {
                      mb: 0.5,
                    },
                  }}
                >
                  <li>Title must be unique for every template.</li>
                  <li>The title cannot exceed 50 characters.</li>
                  <li>
                    Supported languages: English, Hindi, Telgu, Tamil, Kannada, Bengali, Gujarati,
                    Malayalam, Marathi, Urdu.
                  </li>
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <InfoIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ display: 'inline' }}>
            Upload a CSV file containing your templates as per the sample CSV below
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'primary',
                cursor: 'pointer',
                fontWeight: 600,
                textDecoration: 'underline',
              }}
              onClick={handleDownload}
            >
              Download Sample CSV
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '150px',
          }}
        >
          <UploadArea
            name="file"
            onDrop={handleFileChange}
            acceptedMimes={{
              'text/csv': ['.csv'],
            }}
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
        {uploadErrors && (
          <Typography
            variant="body2"
            sx={{
              color: 'error.main',
              cursor: 'pointer',
              fontWeight: 600,
              textDecoration: 'underline',
            }}
            onClick={handleDownloadErrors}
          >
            Download Error Report
          </Typography>
        )}
        <Box
          sx={{
            width: '10rem',
            backgroundColor: !loadingSubmit ? 'primary.main' : '#8590A2',
            borderRadius: '8px',
            p: '0.562rem',
            height: '2.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: !loadingSubmit ? 'auto' : 'none',
          }}
          onClick={handleSubmit(onSubmit)}
        >
          {!loadingSubmit && (
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: '1.43',
                color: '#fff',
              }}
            >
              Submit for Approval
            </Typography>
          )}
          {loadingSubmit && <CircularProgress color="inherit" size={14} sx={{ color: '#fff' }} />}
        </Box>
      </Box>
    </FormProvider>
  );
};

export default BulkTemplateForm;
